import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCostOptimizationService } from "../../../backend";
import type { UpsertSchedulerRequest } from "../../../backend/cost-optimizer";

const keys = {
    schedulers: (projectId: string) => ["cost-optimizer", projectId, "schedulers"] as const,
    resources: (projectId: string, environmentId: string) => ["cost-optimizer", projectId, environmentId, "resources"] as const,
    operations: (projectId: string, environmentId: string) => ["cost-optimizer", projectId, environmentId, "operations"] as const
};

export function useSchedulers(projectId: string, enabled = true) {
    return useQuery({
        queryKey: keys.schedulers(projectId),
        queryFn: async () => (await getCostOptimizationService().listSchedulers(projectId)).data.schedulers,
        enabled: Boolean(projectId && enabled)
    });
}

export function useSchedulerResources(projectId: string, environmentId?: string) {
    return useQuery({
        queryKey: keys.resources(projectId, environmentId || ""),
        queryFn: async () => (await getCostOptimizationService().listResources(projectId, environmentId!)).data,
        enabled: Boolean(projectId && environmentId),
        refetchInterval: query => {
            const status = query.state.data?.scheduler.latestOperation?.status;
            return status === "queued" || status === "running" ? 5000 : 30000;
        }
    });
}

export function useSchedulerOperations(projectId: string, environmentId?: string) {
    return useQuery({
        queryKey: keys.operations(projectId, environmentId || ""),
        queryFn: async () => (await getCostOptimizationService().listOperations(projectId, environmentId!)).data.operations,
        enabled: Boolean(projectId && environmentId),
        refetchInterval: query => query.state.data?.some(operation =>
            operation.status === "queued" || operation.status === "running"
        ) ? 5000 : 30000
    });
}

export function useSchedulerMutations(projectId: string, environmentId?: string) {
    const queryClient = useQueryClient();
    const refresh = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.schedulers(projectId) }),
            environmentId ? queryClient.invalidateQueries({ queryKey: keys.resources(projectId, environmentId) }) : Promise.resolve(),
            environmentId ? queryClient.invalidateQueries({ queryKey: keys.operations(projectId, environmentId) }) : Promise.resolve()
        ]);
    };

    return {
        save: useMutation({
            mutationFn: ({ targetEnvironmentId, request }: { targetEnvironmentId: string; request: UpsertSchedulerRequest }) =>
                getCostOptimizationService().upsertScheduler(projectId, targetEnvironmentId, request),
            onSuccess: refresh
        }),
        start: useMutation({
            mutationFn: (leaseMinutes?: number) => getCostOptimizationService().start(projectId, environmentId!, leaseMinutes),
            onSuccess: refresh
        }),
        stop: useMutation({
            mutationFn: () => getCostOptimizationService().stop(projectId, environmentId!),
            onSuccess: refresh
        }),
        extend: useMutation({
            mutationFn: (durationMinutes: number) => getCostOptimizationService().extendLease(projectId, environmentId!, durationMinutes),
            onSuccess: refresh
        }),
        remove: useMutation({
            mutationFn: () => getCostOptimizationService().deleteScheduler(projectId, environmentId!),
            onSuccess: refresh
        })
    };
}
